import type { DerivedPersona } from '@/types/consumer';

interface DerivedPersonaCardProps {
  persona: DerivedPersona;
  language: string;
  className?: string;
}

export function DerivedPersonaCard({
  persona,
  language,
  className = '',
}: DerivedPersonaCardProps) {
  const t = language === 'es';

  const segmentColors: Record<string, string> = {
    'Traditional Loyalist': 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    'Value Seeker': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Health-Conscious': 'bg-success-500/20 text-success-400 border-success-500/30',
    'Convenience-Driven': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Young Experimenter': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };

  const segmentColorClass =
    segmentColors[persona.matchedSegment] ||
    'bg-surface-600 text-text-secondary border-surface-500';

  // Use calculated values (these are now outputs, not inputs)
  const priceSensitivity = persona.calculatedPriceSensitivity ?? 0.5;
  const brandLoyalty = persona.calculatedBrandLoyalty ?? 0.5;

  return (
    <div className={`card p-5 ${className}`}>
      {/* Header with Avatar */}
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500/30 to-primary-700/30 flex items-center justify-center text-2xl flex-shrink-0">
          {persona.avatar || '👤'}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white">{persona.name}</h4>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <span>{persona.location}</span>
          </div>
        </div>
      </div>

      {/* Matched Segment Badge */}
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${segmentColorClass}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {persona.matchedSegment}
        </span>
        <span className="ml-2 text-xs text-text-tertiary">
          {Math.round(persona.matchScore * 100)}% {t ? 'coincidencia' : 'match'}
        </span>
      </div>

      {/* Quote */}
      <blockquote className="mb-4 pl-3 border-l-2 border-primary-500/50 italic text-sm text-text-secondary">
        "{persona.quote}"
      </blockquote>

      {/* Calculated Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <MetricBox
          label={t ? 'Sensibilidad Precio' : 'Price Sensitivity'}
          value={`${Math.round(priceSensitivity * 100)}%`}
          color={priceSensitivity > 0.7 ? 'warning' : 'default'}
        />
        <MetricBox
          label={t ? 'Lealtad Marca' : 'Brand Loyalty'}
          value={`${Math.round(brandLoyalty * 100)}%`}
          color={brandLoyalty > 0.7 ? 'success' : 'default'}
        />
        <MetricBox
          label={t ? 'Frecuencia' : 'Frequency'}
          value={persona.derivedMetrics.purchaseFrequency}
        />
        <MetricBox
          label={t ? 'Canal' : 'Channel'}
          value={persona.derivedMetrics.channelPreference}
        />
      </div>

      {/* Key Factors (What they care about) */}
      {persona.targetingInfo?.keyFactors && persona.targetingInfo.keyFactors.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-text-tertiary mb-2">
            {t ? 'Factores de Decisión Clave' : 'Key Decision Factors'}
          </p>
          <div className="space-y-1.5">
            {persona.targetingInfo.keyFactors.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-xs text-text-secondary">{item.factor}</span>
                </div>
                <div className="w-20 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full"
                    style={{ width: `${item.importance * 100}%` }}
                  />
                </div>
                <span className="text-xs text-text-tertiary w-8">
                  {Math.round(item.importance * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Media Channels (How to reach them) */}
      {persona.targetingInfo?.mediaChannels && persona.targetingInfo.mediaChannels.length > 0 && (
        <div className="mb-4 pt-3 border-t border-surface-700">
          <p className="text-xs text-text-tertiary mb-2">
            {t ? 'Canales de Medios (Cómo alcanzarlos)' : 'Media Channels (How to reach them)'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {persona.targetingInfo.mediaChannels.slice(0, 4).map((item, idx) => (
              <span
                key={idx}
                className={`text-xs px-2 py-1 rounded border ${
                  item.affinity > 0.6
                    ? 'bg-primary-500/20 text-primary-300 border-primary-500/30'
                    : 'bg-surface-700 text-text-secondary border-surface-600'
                }`}
              >
                {item.channel}
                <span className="ml-1 opacity-60">{Math.round(item.affinity * 100)}%</span>
              </span>
            ))}
          </div>
          {persona.targetingInfo.bestTimeToReach && (
            <p className="text-xs text-text-tertiary mt-2">
              {t ? 'Mejor momento' : 'Best time'}:{' '}
              <span className="text-text-secondary">{persona.targetingInfo.bestTimeToReach}</span>
            </p>
          )}
        </div>
      )}

      {/* Messaging Tips */}
      {persona.targetingInfo?.messagingTips && persona.targetingInfo.messagingTips.length > 0 && (
        <div className="mb-4 pt-3 border-t border-surface-700">
          <p className="text-xs text-text-tertiary mb-2">
            {t ? 'Tips de Mensajería' : 'Messaging Tips'}
          </p>
          <ul className="space-y-1">
            {persona.targetingInfo.messagingTips.map((tip, idx) => (
              <li key={idx} className="text-xs text-text-secondary flex items-start gap-1.5">
                <span className="text-primary-400 mt-0.5">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Characteristics */}
      {persona.topics && persona.topics.length > 0 && (
        <div className="pt-3 border-t border-surface-700">
          <p className="text-xs text-text-tertiary mb-2">
            {t ? 'Características' : 'Characteristics'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {persona.topics.map((topic, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-0.5 bg-surface-700 text-text-secondary rounded"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Brand Preference */}
      <div className="mt-4 pt-3 border-t border-surface-700">
        <p className="text-xs text-text-tertiary">
          {t ? 'Marca preferida' : 'Preferred brand'}:{' '}
          <span className="text-white font-medium">
            {persona.selections.brandPreference || (t ? 'Cualquiera' : 'Any')}
          </span>
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function MetricBox({
  label,
  value,
  color = 'default',
}: {
  label: string;
  value: string;
  color?: 'default' | 'success' | 'warning';
}) {
  const colorClasses = {
    default: 'text-white',
    success: 'text-success-400',
    warning: 'text-warning-400',
  };

  return (
    <div className="p-2.5 bg-surface-700/50 rounded-lg">
      <div className={`font-semibold text-sm ${colorClasses[color]}`}>{value}</div>
      <div className="text-xs text-text-tertiary">{label}</div>
    </div>
  );
}
